import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import snakeCase from 'lodash/snakeCase';
import {
  exportComponentAsJPEG,
  exportComponentAsPDF,
  exportComponentAsPNG
} from 'react-component-export-image';

import makeStyles from '@material-ui/core/styles/makeStyles';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SendIcon from '@material-ui/icons/Send';

import {
  MainSettingsMenu,
  FormattingMenu,
  ExportMenu,
  MoreOptionsMenu
} from 'components/Widget/components/menus';
import BaseWidgetActionsPopper from 'components/BaseWidgetActionsPopper';
import {
  parseObjectValuesToBool,
  calculatePdfImageSizeInMM,
  calculatePdfImageCoordinatesInMM
} from 'utils/widget-helpers';
import WidgetButton from '../../WidgetButton';

const useStyles = makeStyles(() => ({
  popper: {
    zIndex: '10000'
  },
  mediumIcon: {
    fontSize: 'medium'
  }
}));

const OptionsButton = ({
  title,
  description,
  masterObjectId,
  masterObjectTypeId,
  isTitleDisplayed,
  isDateDisplayed,
  isLegendDisplayed,
  isChartTooltipDisplayed,
  onMainSettingsSubmit,
  onMoreOptionsSubmit,
  benchmarks,
  tableExportData,
  dashboardData,
  timeSeriesSetId,
  widgetRef,
  widgetOverlayLists,
  setFilters
}) => {
  const classes = useStyles();
  const { register, handleSubmit } = useForm();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuState, setMenuState] = useState();
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClick = event => {
    setMenuAnchorEl(event.currentTarget);
    setMenuState(event.currentTarget.id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuState(null);
  };

  const exportChart = useCallback(
    e => {
      widgetRef.current.chartObj.exportChart({
        exportFormat: e.currentTarget.value
      });
    },
    [widgetRef]
  );

  const exportTable = useCallback(
    (fileFormat, fileName) => {
      if (fileFormat === 'pdf') {
        const { h, w } = calculatePdfImageSizeInMM(
          widgetRef.current.offsetHeight,
          widgetRef.current.offsetWidth
        );
        const { x, y } = calculatePdfImageCoordinatesInMM(
          widgetRef.current.offsetHeight,
          widgetRef.current.offsetWidth
        );

        exportComponentAsPDF(widgetRef, {
          fileName: snakeCase(fileName),
          pdfOptions: {
            orientation: 'l',
            unit: 'mm',
            w,
            h,
            y,
            x
          }
        });
      }
      if (fileFormat === 'jpg') {
        exportComponentAsJPEG(widgetRef, { fileName: snakeCase(fileName) });
      }
      if (fileFormat === 'png') {
        exportComponentAsPNG(widgetRef, { fileName: snakeCase(fileName) });
      }
      handleClose();
    },
    [widgetRef]
  );

  const handleMainSettingsSubmit = formData => {
    onMainSettingsSubmit(formData);
    handleClose();
  };

  const handleMoreOptionsSubmit = checkboxData => {
    const parsedCheckboxData = parseObjectValuesToBool(checkboxData);

    onMoreOptionsSubmit(parsedCheckboxData);
    handleClose();
  };

  return (
    <>
      <WidgetButton onClick={handleClick}>
        <SettingsIcon className={classes.mediumIcon} />
      </WidgetButton>
      <Popper
        open={!!anchorEl}
        placement="bottom-end"
        anchorEl={anchorEl}
        transition
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper>
            <MenuList>
              <MenuItem onClick={handleMenuClick} id="menuSettings">
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">Main Settings</Typography>
              </MenuItem>
              <MenuItem onClick={handleMenuClick} id="ExportingSettings">
                <ListItemIcon>
                  <SaveAltRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit" noWrap>
                  Exporting
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleMenuClick} id="moreOptions">
                <ListItemIcon>
                  <BuildOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit" noWrap>
                  More Options
                </Typography>
              </MenuItem>
            </MenuList>

            <BaseWidgetActionsPopper anchorEl={menuAnchorEl} isOpen={menuState === 'menuSettings'}>
              <MainSettingsMenu
                onSubmit={handleMainSettingsSubmit}
                handleClose={handleClose}
                title={title}
                description={description}
                masterObjectId={masterObjectId}
                masterObjectTypeId={masterObjectTypeId}
                benchmarks={benchmarks}
                timeSeriesSetId={timeSeriesSetId}
                dashboardData={dashboardData}
                widgetOverlayLists={widgetOverlayLists}
                setFilters={setFilters}
              />
            </BaseWidgetActionsPopper>

            <BaseWidgetActionsPopper
              anchorEl={menuAnchorEl}
              isOpen={menuState === 'formattingSettings'}
            >
              <FormattingMenu handleClose={handleClose} />
            </BaseWidgetActionsPopper>

            <BaseWidgetActionsPopper
              anchorEl={menuAnchorEl}
              isOpen={menuState === 'ExportingSettings'}
            >
              <ExportMenu
                tableData={tableExportData}
                exportChart={exportChart}
                handleClose={handleClose}
                exportTable={exportTable}
              />
            </BaseWidgetActionsPopper>

            <BaseWidgetActionsPopper anchorEl={menuAnchorEl} isOpen={menuState === 'moreOptions'}>
              <MoreOptionsMenu
                onSubmit={handleSubmit(handleMoreOptionsSubmit)}
                register={register}
                handleClose={handleClose}
                isLegendDisplayed={isLegendDisplayed}
                isTitleDisplayed={isTitleDisplayed}
                isChartTooltipDisplayed={isChartTooltipDisplayed}
                isDateDisplayed={isDateDisplayed}
              />
            </BaseWidgetActionsPopper>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

OptionsButton.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  masterObjectId: PropTypes.number.isRequired,
  masterObjectTypeId: PropTypes.number,
  isTitleDisplayed: PropTypes.bool.isRequired,
  isDateDisplayed: PropTypes.bool.isRequired,
  isLegendDisplayed: PropTypes.bool.isRequired,
  isChartTooltipDisplayed: PropTypes.bool.isRequired,
  onMainSettingsSubmit: PropTypes.func.isRequired,
  onMoreOptionsSubmit: PropTypes.func.isRequired,
  // isTableWidget: PropTypes.bool.isRequired,
  benchmarks: PropTypes.arrayOf(
    PropTypes.shape({
      benchmarkId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  tableExportData: PropTypes.shape({
    title: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    rows: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
      ])
    )
  }),
  dashboardData: PropTypes.shape({
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number.isRequired,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        benchmarkId: PropTypes.number.isRequired
      })
    )
  }),
  widgetRef: PropTypes.shape({ current: PropTypes.any }),
  widgetFiltersListData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    text: PropTypes.string,
    filtersListMembers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        listId: PropTypes.string,
        memberText: PropTypes.string
      })
    )
  })
  // tableColumnsState: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

OptionsButton.defaultProps = {
  title: null,
  description: null,
  masterObjectTypeId: null,
  benchmarks: null,
  tableExportData: null,
  dashboardData: {},
  widgetRef: null
};

export default OptionsButton;
